import {
  InputField,
  SearchableCard,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { RefObject } from "react";
import { PORTAL_ID_OPTIONS } from "../constants";

interface PortalIdFieldProps {
  portalTarget: RefObject<HTMLDivElement>;
  portalId: string;
  portalIdError: string | undefined;
  onPortalIdChange: (value: string) => void;
  onPortalIdBlur: () => void;
  facilityName: string;
  facilityError: string | undefined;
  onFacilityChange: (value: string) => void;
  onFacilityBlur: () => void;
  locationId: string;
  locationError: string | undefined;
  onLocationChange: (value: string) => void;
  onLocationBlur: () => void;
}

/**
 * Portal ID picker plus the OncoEmr-only facility and location companion
 * fields. Rendered together because those fields are semantically a
 * sub-input of the portal selection and only appear when `oncoemr` is
 * chosen.
 *
 * The wrapping `<div>` carries an `onBlur` that fires once focus has left
 * the entire SearchableCard (trigger + inline popover); the
 * `relatedTarget` check prevents the touched flag from flipping while
 * focus is moving between the trigger and the dropdown.
 */
export const PortalIdField = ({
  portalTarget,
  portalId,
  portalIdError,
  onPortalIdChange,
  onPortalIdBlur,
  facilityName,
  facilityError,
  onFacilityChange,
  onFacilityBlur,
  locationId,
  locationError,
  onLocationChange,
  onLocationBlur,
}: PortalIdFieldProps) => (
  <>
    <div
      ref={portalTarget}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          onPortalIdBlur();
        }
      }}
    >
      <SearchableCard
        id="portal_id"
        label="Portal ID"
        required
        size="medium"
        shouldUsePortal={false}
        placeholder="Select Portal ID"
        searchPlaceholder="Search portal IDs"
        hint="Unique identifier used internally to reference this portal."
        error={portalIdError}
        value={portalId}
        defaultValue={portalId}
        options={PORTAL_ID_OPTIONS}
        onChange={onPortalIdChange}
        portalTarget={portalTarget}
        keepTriggerMounted
      />
    </div>
    {portalId === "oncoemr" && (
      <>
        <InputField
          label="Facility Location in OncoEmr"
          size="md"
          fullWidth
          required
          hint="Facility location as configured in OncoEmr for this credential."
          error={facilityError}
          defaultValue={facilityName}
          id="facility_name"
          onChange={(e) => onFacilityChange(e.target.value)}
          onBlur={onFacilityBlur}
        />
        <InputField
          label="Location ID in OncoEmr"
          size="md"
          fullWidth
          required
          hint="OncoEMR locationId used to open patient charts (for example LH_...)."
          error={locationError}
          defaultValue={locationId}
          id="location_id"
          onChange={(e) => onLocationChange(e.target.value)}
          onBlur={onLocationBlur}
        />
      </>
    )}
  </>
);
